// Visual hints only — the backend is the authority on completeness.
// Mirrors libs/nest-lib/src/lib/constants/kyc-requirements.constants.ts.

/**
 * Roles where role_verified gates real-world behaviour (e.g. store products
 * visibility, consultant being bookable). Other roles like `individual` and
 * the admin tiers don't need a "go-live" flow — once the user is email-
 * verified they can take action.
 */
export const LIVE_RELEVANT_ROLES = new Set<string>([
  'store_owner',
  'consultant',
]);

export const ROLE_REQUIRED_DOCS: Record<string, string[]> = {
  store_owner: [
    'CAC',
    'PROOF_OF_ADDRESS',
    'TAX_IDENTIFICATION_NUMBER',
    'CAC_STATUS_REPORT',
    'MEMORANDUM_AND_ARTICLES',
  ],
  individual: [],
  consultant: ['MEDICAL_LICENSE'],
  admin: [],
  super_admin: [],
};

export const USER_REQUIRED_DOCS: Record<string, string[]> = {
  store_owner: ['NIN', 'BVN', 'UTILITY_BILL'],
  individual: ['NIN', 'BVN'],
  consultant: ['NIN', 'BVN', 'UTILITY_BILL'],
  admin: [],
  super_admin: [],
};

/**
 * A user qualifies for the "verified" tick when they're email-verified, not
 * suspended, and at least one of their roles is operational:
 *   - individual / admin / super_admin → email verification is enough
 *   - consultant / store_owner → the role must be live (role_verified)
 *
 * This intentionally mirrors what each role can actually *do* in the apps,
 * so a half-onboarded consultant who's only ever email-verified does NOT
 * get the tick.
 */
export function isUserVerified(input: {
  is_verified: boolean;
  suspended_at?: string | null;
  roles: { role_type: string; role_verified: boolean }[];
}): boolean {
  if (input.suspended_at) return false;
  if (!input.is_verified) return false;
  if (!input.roles?.length) return false;
  return input.roles.some((r) =>
    LIVE_RELEVANT_ROLES.has(r.role_type) ? r.role_verified : true
  );
}
